import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, ArrowRight } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Lead = {
  id: number
  first_name: string
  last_name: string
  full_name: string
  email: string
  job_title: string
  company_name: string
  industry: string
  source: 'WEBSITE' | 'REFERRAL' | 'COLD_CALL' | 'EVENT' | 'SOCIAL' | 'EMAIL_CAMPAIGN' | 'OTHER'
  status: 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'UNQUALIFIED' | 'CONVERTED'
  score: number
  owner_username: string | null
  converted_account: number | null
  converted_account_name: string | null
  converted_opportunity_name: string | null
  converted_at: string | null
}

// Minimal create payload — full editing happens on the record page.
type CreateInput = {
  first_name: string
  last_name: string
  email: string
  phone: string
  job_title: string
  company_name: string
  source: Lead['source']
  status: Lead['status']
  score: number
}

const STATUS_COLOR: Record<Lead['status'], string> = {
  NEW: 'bg-blue-100 text-blue-800',
  CONTACTED: 'bg-amber-100 text-amber-800',
  QUALIFIED: 'bg-purple-100 text-purple-800',
  UNQUALIFIED: 'bg-gray-100 text-gray-600',
  CONVERTED: 'bg-green-100 text-green-800',
}

const SOURCE_LABEL: Record<Lead['source'], string> = {
  WEBSITE: 'Website',
  REFERRAL: 'Referral',
  COLD_CALL: 'Cold call',
  EVENT: 'Event',
  SOCIAL: 'Social',
  EMAIL_CAMPAIGN: 'Email',
  OTHER: 'Other',
}

const STATUS_FILTERS: ('ALL' | Lead['status'])[] = [
  'ALL', 'NEW', 'CONTACTED', 'QUALIFIED', 'UNQUALIFIED', 'CONVERTED',
]

export default function CRMLeads() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const { data, isLoading } = useQuery({
    queryKey: ['crm', 'leads'],
    queryFn: async () => (await api.get<Lead[]>('/crm/leads/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStatus, setFilterStatus] = useState<'ALL' | Lead['status']>('ALL')

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Lead>('/crm/leads/', {
      ...draft,
      company_size: '',
      industry: '',
      website: '',
      country_code: '',
      owner: null,
      notes: '',
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['crm', 'leads'] })
      toast.success('Lead created')
      setCreating(false)
      navigate(`/admin/crm/leads/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/crm/leads/${id}/`),
    onSuccess: () => {
      toast.success('Lead deleted')
      qc.invalidateQueries({ queryKey: ['crm', 'leads'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const leads = data || []
  const filtered = filterStatus === 'ALL' ? leads : leads.filter((l) => l.status === filterStatus)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Leads</h2>
          <p className="text-sm text-muted-foreground">
            {leads.length} total · {leads.filter((l) => l.status === 'NEW').length} new ·{' '}
            {leads.filter((l) => l.status === 'CONVERTED').length} converted
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> New lead</Button>
      </div>

      <div className="mb-4 flex items-center space-x-2 text-sm flex-wrap gap-y-2">
        <span className="text-muted-foreground">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No leads.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Person</Th>
                <Th>Company</Th>
                <Th>Source</Th>
                <Th className="text-right">Score</Th>
                <Th>Status</Th>
                <Th>Owner</Th>
                <Th>Converted to</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((l) => (
                <tr
                  key={l.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/crm/leads/${l.id}`)}
                >
                  <Td>
                    <div className="font-medium text-foreground">{l.full_name || <span className="italic text-muted-foreground">Unnamed</span>}</div>
                    {l.email && <div className="text-xs text-muted-foreground">{l.email}</div>}
                    {l.job_title && <div className="text-xs text-muted-foreground">{l.job_title}</div>}
                  </Td>
                  <Td>
                    <div>{l.company_name || <span className="text-muted-foreground">—</span>}</div>
                    {l.industry && <div className="text-xs text-muted-foreground">{l.industry}</div>}
                  </Td>
                  <Td className="text-xs">{SOURCE_LABEL[l.source]}</Td>
                  <Td className="text-right">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono ${
                      l.score >= 70 ? 'bg-red-100 text-red-800' :
                      l.score >= 40 ? 'bg-amber-100 text-amber-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>{l.score}</span>
                  </Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[l.status]}`}>
                      {l.status}
                    </span>
                  </Td>
                  <Td className="text-xs">{l.owner_username || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">
                    {l.converted_at ? (
                      <div>
                        <div className="inline-flex items-center gap-1">
                          <ArrowRight className="h-3 w-3 text-muted-foreground" /> {l.converted_account_name}
                        </div>
                        {l.converted_opportunity_name && (
                          <div className="text-muted-foreground">+ {l.converted_opportunity_name}</div>
                        )}
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/crm/leads/${l.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={l.status === 'CONVERTED'}
                          onClick={() => l.status !== 'CONVERTED' && confirm(
                            `lead ${l.full_name}`,
                            () => deleteMut.mutate(l.id),
                          )}
                          className={l.status !== 'CONVERTED' ? 'text-destructive' : ''}
                        >
                          {l.status === 'CONVERTED' ? 'Delete (converted)' : 'Delete'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Rest filled in on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New lead"
        description="Capture the essentials. Company size, industry, website, country, and notes are filled in on the lead's record page. Convert the lead from there once it's qualified."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ld-first">First name *</FieldLabel>
              <Input id="ld-first" autoFocus required value={draft.first_name}
                onChange={(ev) => setDraft({ ...draft, first_name: ev.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ld-last">Last name</FieldLabel>
              <Input id="ld-last" value={draft.last_name}
                onChange={(ev) => setDraft({ ...draft, last_name: ev.target.value })} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ld-email">Email</FieldLabel>
              <Input id="ld-email" type="email" value={draft.email}
                onChange={(ev) => setDraft({ ...draft, email: ev.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ld-phone">Phone</FieldLabel>
              <Input id="ld-phone" type="tel" value={draft.phone}
                onChange={(ev) => setDraft({ ...draft, phone: ev.target.value })} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="ld-job">Job title</FieldLabel>
            <Input id="ld-job" value={draft.job_title}
              onChange={(ev) => setDraft({ ...draft, job_title: ev.target.value })} />
          </Field>

          <Field>
            <FieldLabel htmlFor="ld-company">Company name</FieldLabel>
            <Input id="ld-company" value={draft.company_name}
              onChange={(ev) => setDraft({ ...draft, company_name: ev.target.value })} />
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="ld-source">Source</FieldLabel>
              <Select value={draft.source}
                onValueChange={(v) => setDraft({ ...draft, source: v as Lead['source'] })}>
                <SelectTrigger id="ld-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="WEBSITE">Website</SelectItem>
                  <SelectItem value="REFERRAL">Referral</SelectItem>
                  <SelectItem value="COLD_CALL">Cold call</SelectItem>
                  <SelectItem value="EVENT">Event</SelectItem>
                  <SelectItem value="SOCIAL">Social media</SelectItem>
                  <SelectItem value="EMAIL_CAMPAIGN">Email campaign</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="ld-status">Status</FieldLabel>
              <Select value={draft.status}
                onValueChange={(v) => setDraft({ ...draft, status: v as Lead['status'] })}>
                <SelectTrigger id="ld-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEW">New</SelectItem>
                  <SelectItem value="CONTACTED">Contacted</SelectItem>
                  <SelectItem value="QUALIFIED">Qualified</SelectItem>
                  <SelectItem value="UNQUALIFIED">Unqualified</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="ld-score">Score (0-100)</FieldLabel>
              <Input id="ld-score" type="number" min={0} max={100} value={draft.score}
                onChange={(ev) => setDraft({ ...draft, score: Number(ev.target.value) })}
                className="font-mono text-right" />
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    first_name: '', last_name: '', email: '', phone: '', job_title: '',
    company_name: '', source: 'WEBSITE', status: 'NEW', score: 0,
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
